import type {
  ApiItem,
  ItemDetailsResponse,
  ImportItemPayload,
  ImportedItem,
  ItemSupplierData,
  ItemVariation,
  CreateItemFormValues,
  DraftItem,
  ItemDetails,
  UpdateItemPayload,
} from "@/app/types/items.types";
import type { Item } from "@/shared/types";
import { ITEM_STATUS } from "@/shared/constants";

export const SORT_COLUMN_TO_PARAM: Record<string, string> = {
  itemName: "name",
  preferredSupplier: "preferred_supplier",
  costPerPurchaseUOM: "cost_per_purchase",
  unitCost: "unit_cost",
};

export const mapSortColumnToParam = (column?: string) =>
  column ? SORT_COLUMN_TO_PARAM[column] || column : undefined;

export const mapApiItemToItem = (apiItem: ApiItem): Item => ({
  id: String(apiItem.id),
  name: apiItem.name,
  nameEn: apiItem.name,
  nameAr: apiItem.name,
  sku: apiItem.sku,
  category: {
    id: apiItem.category,
    name: apiItem.category,
    code: apiItem.category,
  },
  subcategory: apiItem.subcategory
    ? {
        id: apiItem.subcategory,
        name: apiItem.subcategory,
        code: apiItem.subcategory,
      }
    : undefined,
  tags: [],
  baseUOM: apiItem.base_uom_short,
  preferredSupplier: undefined,
  costPerPurchaseUOM: apiItem.purchase_cost ?? 0,
  purchaseUOM: apiItem.purchase_uom,
  caseSize: apiItem.pack_size ? String(apiItem.pack_size) : undefined,
  unitCost: apiItem.unit_cost ?? 0,
  status: apiItem.status,
  inUse: apiItem.in_use,
  created_at: apiItem.created_at,
  updated_at: apiItem.updated_at,
  preferred_supplier: apiItem.preferred_supplier,
});

/**
 * Extract ID from "name - id" format used in Excel templates
 * @example extractIdFromFormat("Kilogram - 1") // returns 1
 * @example extractIdFromFormat("Proteins – Seafood - 19") // returns 19
 */
export function extractIdFromFormat(optionString: string): number | null {
  const parts = optionString.split(" - ");
  if (parts.length < 2) return null;
  const id = parseInt(parts[parts.length - 1].trim(), 10);
  return isNaN(id) ? null : id;
}

export const mapApiItemDetailToItem = (
  apiItemDetail: ItemDetailsResponse,
): Item => ({
  id: String(apiItemDetail.id),
  name: apiItemDetail.name_en || apiItemDetail.name,
  nameEn: apiItemDetail.name_en,
  nameAr: apiItemDetail.name_ar,
  sku: apiItemDetail.sku,
  category: {
    id: String(apiItemDetail.category.id),
    name: apiItemDetail.category.name,
    code: apiItemDetail.category.code ?? "",
  },
  subcategory: apiItemDetail.subcategory
    ? {
        id: String(apiItemDetail.subcategory.id),
        name: apiItemDetail.subcategory.name,
        code: apiItemDetail.subcategory.code ?? "",
      }
    : undefined,
  tags: (apiItemDetail.tags ?? []).map((tag) => tag.name),
  tagIds: (apiItemDetail.tags ?? []).map((tag) => tag.id),
  baseUOM: apiItemDetail.base_uom.short_name,
  baseUomId: String(apiItemDetail.base_uom.id),
  baseUomName: apiItemDetail.base_uom.name,
  preferredSupplier: apiItemDetail?.preferred_supplier || null,
  backupSuppliers: apiItemDetail.backup_suppliers || [],
  costPerPurchaseUOM: apiItemDetail?.purchase_cost ?? 0,
  purchaseUOM: apiItemDetail.purchase_uom.name,
  purchaseUomId: String(apiItemDetail.purchase_uom.id),
  caseSize: apiItemDetail.pack_size
    ? String(apiItemDetail.pack_size)
    : undefined,
  packSize: apiItemDetail.pack_size,
  unitCost: apiItemDetail.unit_cost ?? 0,
  status: apiItemDetail.status as "active" | "inactive",
  parLevel: apiItemDetail.per_level || 0,
  inUse: apiItemDetail.in_use,
  activityLog: apiItemDetail.audit_logs || [],
  created_at: apiItemDetail.created_at,
  updated_at: apiItemDetail.updated_at,
});

export function mapLoadedItemToFormItem(item: DraftItem): ItemDetails {
  return {
    name_en: item.name_en || "",
    name_ar: item.name_ar || "",
    base_u_o_m_id: item.base_uom?.id?.toString() || "",
    purchase_u_o_m_id: item.purchase_uom?.id?.toString() || "",
    base_u_o_m_label: item.base_uom?.name || "",
    purchase_u_o_m_label: item.purchase_uom?.name || "",
    pack_size: item.pack_size === 0 ? undefined : item.pack_size,
    per_level: item.per_level === 0 ? undefined : item.per_level,
    tag_ids: item.tags?.map((tag) => tag.id) || [],
    in_use: item.in_use || false,
    item_id: item.id,
    sku: item.sku,
  };
}

export function mapItemDetailsToCreateForm(item: Item): CreateItemFormValues {
  return {
    category: String(item.category.id),
    categoryLabel: item.category.name,
    subcategory_id: item.subcategory?.id ? String(item.subcategory.id) : "",
    subCategoryLabel: item.subcategory?.name ?? "",
    status:
      item.status === ITEM_STATUS.INACTIVE
        ? ITEM_STATUS.INACTIVE
        : ITEM_STATUS.ACTIVE,
    items: [
      {
        name_en: item.nameEn,
        name_ar: item.nameAr,
        base_u_o_m_id: item.baseUomId ? String(item.baseUomId) : "",
        purchase_u_o_m_id: item.purchaseUomId ? String(item.purchaseUomId) : "",
        base_u_o_m_label: item.baseUomName ?? item.baseUOM,
        purchase_u_o_m_label: item.purchaseUOM,
        pack_size:
          item.packSize != null
            ? Number(item.packSize)
            : item.caseSize
              ? Number(item.caseSize)
              : undefined,
        per_level:
          item.parLevel != null && item.parLevel !== undefined
            ? Number(item.parLevel)
            : undefined,
        tag_ids: (item.tagIds ?? []).map((id) => Number(id)),
        in_use: item.inUse ?? false,
        item_id: Number(item.id),
        sku: item.sku,
      },
    ],
  };
}

export function itemHasDetails(item: ItemDetails): boolean {
  return Boolean(
    item.base_u_o_m_id ||
      item.purchase_u_o_m_id ||
      item.pack_size !== undefined ||
      item.per_level !== undefined ||
      (item.tag_ids && item.tag_ids.length > 0),
  );
}

function toNumberId(value: string | number | undefined): number {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function buildItemUpdatePayload(
  formValues: CreateItemFormValues,
): UpdateItemPayload {
  const item = formValues.items[0];

  return {
    subcategory_id: toNumberId(formValues.subcategory_id),
    name_ar: item?.name_ar ?? "",
    name_en: item?.name_en ?? "",
    base_u_o_m_id: toNumberId(item?.base_u_o_m_id),
    purchase_u_o_m_id: toNumberId(item?.purchase_u_o_m_id),
    pack_size: Number(item?.pack_size ?? 0),
    per_level: Number(item?.per_level ?? 0),
    status: formValues.status,
    in_use: item?.in_use ?? false,
    tag_ids: (item?.tag_ids ?? []).map((id) => Number(id)),
  };
}

/**
 * Map API audit logs to ActivityLogEntry format for UI display
 * Note: Titles will be translated in the ItemActivityLog component
 */
export { mapAuditLogsToActivityEntries } from "./itemActivityLog.utils";

export const transformItemsForImport = (
  items: ImportedItem[],
): ImportItemPayload[] => {
  return items.map((item, index) => {
    const subcategoryId = extractIdFromFormat(item.subcategory);
    const baseUomId = extractIdFromFormat(item.baseUOM);
    const purchaseUomId = extractIdFromFormat(item.purchaseUOM);
    const tagIds = (item.tags || [])
      .map((tagName) => extractIdFromFormat(tagName))
      .filter((id): id is number => id !== null);
    if (!subcategoryId) {
      console.warn(
        `Item ${index}: Could not extract subcategory ID from "${item.subcategory}"`,
      );
    }
    if (!baseUomId) {
      console.warn(
        `Item ${index}: Could not extract base UOM ID from "${item.baseUOM}"`,
      );
    }
    if (!purchaseUomId) {
      console.warn(
        `Item ${index}: Could not extract purchase UOM ID from "${item.purchaseUOM}"`,
      );
    }

    const transformedItem = {
      subcategory_id: subcategoryId || 0,
      name_ar: item.nameAr,
      name_en: item.nameEn,
      base_u_o_m_id: baseUomId || 0,
      purchase_u_o_m_id: purchaseUomId || 0,
      pack_size: item.packSize,
      sku: "",
      per_level: item.parLevel,
      tag_ids: tagIds,
      in_use: false,
    };


    return transformedItem;
  });
};


export function getSupplierItemVariations(
  supplier: ItemSupplierData,
  itemId?: number,
): ItemVariation[] {
  if (!itemId) return [];
  const supplierItem = supplier.items.find((item) => item.id === itemId);
  if (!supplierItem) return [];
  return supplierItem.variations || [];
}
